import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {AppLayoutComponent} from './app-layout.component';
import {requiredAuthorizationGuard} from './guards/required-authorization.guard';
import {requiredPermissionsGuard} from './guards/required-permissions.guard';
import {requiredRolesGuard} from './guards/required-roles.guard';
import {requireClustersGuard} from './guards/require-clusters.guard';
import {loadImagesGuard} from './guards/images-loader.guard';
import {loadIconsGuard} from './guards/icons-loader.guard';


const routes: Routes = [
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [requiredAuthorizationGuard, loadImagesGuard, loadIconsGuard],
    children: [
      {path: '', pathMatch: 'full', redirectTo: 'dashboard'},
      {
        path: 'dashboard',
        loadChildren: () => import('./pages/dashboard/dashboard.module')
          .then(module => module.DashboardModule),
        canActivate: [requireClustersGuard]
      },
      {
        path: 'clusters', data: {feature: 'clusters', permissionMode: 'read'},
        loadChildren: () => import('./pages/clusters/clusters.module')
          .then(module => module.ClustersModule),
        canActivate: [requireClustersGuard]
      },
      {
        path: 'clusters/new', data: {mode: 'new', feature: 'clusters', permissionMode: 'create'},
        loadChildren: () => import('./pages/manage-cluster/manage-cluster.module')
          .then(module => module.ManageClusterModule),
        canActivate: [requiredPermissionsGuard]
      },
      {
        path: 'streams', data: {feature: 'streams', permissionMode: 'read'},
        loadChildren: () => import('./pages/inputs/inputs.module')
          .then(module => module.InputsModule),
        canActivate: [requireClustersGuard]
      },
      {
        path: 'streams/new', data: {mode: 'new', feature: 'streams', permissionMode: 'create'},
        loadChildren: () => import('./pages/manage-input/manage-input.module')
          .then(module => module.ManageInputModule),
        canActivate: [requiredPermissionsGuard, requireClustersGuard]
      },
      {
        path: 'streams/new/:id', data: {mode: 'new-duplicate', feature: 'streams', permissionMode: 'create'},
        loadChildren: () => import('./pages/manage-input/manage-input.module')
          .then(module => module.ManageInputModule),
        canActivate: [requiredPermissionsGuard, requireClustersGuard]
      },
      {
        path: 'streams/edit/:id', data: {mode: 'edit', feature: 'streams', permissionMode: 'edit'},
        loadChildren: () => import('./pages/manage-input/manage-input.module')
          .then(module => module.ManageInputModule),
        canActivate: [requiredPermissionsGuard, requireClustersGuard]
      },
      {
        path: 'destinations', data: {feature: 'destinations', permissionMode: 'read'},
        loadChildren: () => import('./pages/destinations/destinations.module')
          .then(module => module.DestinationsModule),
        canActivate: [requireClustersGuard]
      },
      {
        path: 'destinations/new', data: {mode: 'new', feature: 'destinations', permissionMode: 'create'},
        loadChildren: () => import('./pages/manage-destination/manage-destination.module')
          .then(module => module.ManageDestinationModule),
        canActivate: [requiredPermissionsGuard, requireClustersGuard]
      },
      {
        path: 'destinations/new/:id', data: {mode: 'new-duplicate', feature: 'destinations', permissionMode: 'create'},
        loadChildren: () => import('./pages/manage-destination/manage-destination.module')
          .then(module => module.ManageDestinationModule),
        canActivate: [requiredPermissionsGuard, requireClustersGuard]
      },
      {
        path: 'destinations/edit/:id', data: {mode: 'edit', feature: 'destinations', permissionMode: 'edit'},
        loadChildren: () => import('./pages/manage-destination/manage-destination.module')
          .then(module => module.ManageDestinationModule),
        canActivate: [requiredPermissionsGuard, requireClustersGuard]
      },
      {
        path: 'multiview', data: {feature: 'grids', permissionMode: 'read'},
        loadChildren: () => import('./pages/grids/grids.module')
          .then(module => module.GridsModule),
        canActivate: [requireClustersGuard]
      },
      {
        path: 'multiview/new', data: {mode: 'new', feature: 'grids', permissionMode: 'create'},
        loadChildren: () => import('./pages/manage-multiview/manage-multiview.module')
          .then(module => module.ManageMultiviewModule),
        canActivate: [requiredPermissionsGuard, requireClustersGuard]
      },
      {
        path: 'multiview/edit/:id', data: {mode: 'edit', feature: 'grids', permissionMode: 'edit'},
        loadChildren: () => import('./pages/manage-multiview/manage-multiview.module')
          .then(module => module.ManageMultiviewModule),
        canActivate: [requiredPermissionsGuard, requireClustersGuard]
      },
      {
        path: 'transcoding-scenarios', data: {feature: 'transcoding-scenarios', permissionMode: 'read'},
        loadChildren: () => import('./pages/transcoding-scenarios/transcoding-scenarios.module')
          .then(module => module.TranscodingScenariosModule)
      },
      {
        path: 'dvr', data: {feature: 'dvr', permissionMode: 'read'},
        loadChildren: () => import('./pages/dvr/dvr.module')
          .then(module => module.DvrModule),
        canActivate: [requireClustersGuard]
      },
      {
        path: 'organizations', data: {access: [10]},
        loadChildren: () => import('./pages/organizations/organizations.module')
          .then(module => module.OrganizationsModule),
        canActivate: [requiredRolesGuard]
      },
      {
        path: 'organizations/new', data: {mode: 'new', access: [10]},
        loadChildren: () => import('./pages/manage-organizations/manage-organizations.module')
          .then(module => module.ManageOrganizationsModule),
        canActivate: [requiredRolesGuard]
      },
      {
        path: 'organizations/edit/:id', data: {mode: 'edit', access: [5, 10]},
        loadChildren: () => import('./pages/manage-organizations/manage-organizations.module')
          .then(module => module.ManageOrganizationsModule),
        canActivate: [requiredRolesGuard]
      },
      {
        path: 'organization', data: {access: [5, 10]},
        loadChildren: () => import('./pages/organization/organization.module')
          .then(m => m.OrganizationModule),
        canActivate: [requiredRolesGuard]
      },
      {
        path: 'organization/edit/:id', data: {mode: 'edit', access: [5, 10]},
        loadChildren: () => import('./pages/manage-organizations/manage-organizations.module')
          .then(module => module.ManageOrganizationsModule),
        canActivate: [requiredRolesGuard]
      },
      {
        path: 'organization/licenses-history', data: {mode: 'edit', access: [5, 10]},
        loadChildren: () => import('./pages/transcoding-licenses-history/transcoding-licenses-history.module')
          .then(module => module.TranscodingLicensesHistoryModule),
        canActivate: [requiredRolesGuard]
      },
      {
        path: 'organization/users/new', data: {mode: 'new', access: [5, 10]},
        loadChildren: () => import('./pages/manage-user/manage-user.module')
          .then(module => module.ManageUserModule),
        canActivate: [requiredRolesGuard]
      },
      {
        path: 'organizations/users/new', data: {mode: 'new', access: [5, 10]},
        loadChildren: () => import('./pages/manage-user/manage-user.module')
          .then(module => module.ManageUserModule),
        canActivate: [requiredRolesGuard]
      },
      {
        path: 'organization/users/edit/:id', data: {mode: 'edit', access: [5, 10]},
        loadChildren: () => import('./pages/manage-user/manage-user.module')
          .then(module => module.ManageUserModule),
        canActivate: [requiredRolesGuard]
      },
      {
        path: 'organizations/users/edit/:id', data: {mode: 'edit', access: [5, 10]},
        loadChildren: () => import('./pages/manage-user/manage-user.module')
          .then(module => module.ManageUserModule),
        canActivate: [requiredRolesGuard]
      },
      {
        path: 'account',
        loadChildren: () => import('./pages/account/account.module')
          .then(module => module.AccountModule)
      },
      {
        path: 'clips',
        loadChildren: () => import('./pages/clips/clips.module')
          .then(module => module.ClipsModule),
        canActivate: [requireClustersGuard]
      },
      {
        path: 'clips/new',
        data: {mode: 'new', feature: 'clips', permissionMode: 'create'},
        loadChildren: () => import('./pages/manage-clip/manage-clip.module')
          .then(module => module.ManageClipModule),
        canActivate: [requiredPermissionsGuard, requireClustersGuard]
      },
      {
        path: 'clips/edit/:id', data: {mode: 'edit', feature: 'clips', permissionMode: 'edit'},
        loadChildren: () => import('./pages/manage-clip/manage-clip.module')
          .then(module => module.ManageClipModule),
        canActivate: [requiredPermissionsGuard, requireClustersGuard]
      },
      {
        path: 'production', data: {feature: 'production', permissionMode: 'read'},
        loadChildren: () => import('./pages/production/production.module')
          .then(module => module.ProductionModule),
        canActivate: [requireClustersGuard]
      },
      {
        path: 'production/new', data: {mode: 'new', feature: 'production', permissionMode: 'create'},
        loadChildren: () => import('./pages/manage-production/manage-production.module')
          .then(module => module.ManageProductionModule),
        canActivate: [requiredPermissionsGuard, requireClustersGuard]
      },
      {
        path: 'production/edit/:id', data: {mode: 'edit', feature: 'production', permissionMode: 'edit'},
        loadChildren: () => import('./pages/manage-production/manage-production.module')
          .then(module => module.ManageProductionModule),
        canActivate: [requiredPermissionsGuard, requireClustersGuard]
      },
      {
        path: 'payment', data: {access: [5]},
        loadChildren: () => import('./pages/payment/payment.module')
          .then(module => module.PaymentModule),
        canActivate: [requiredRolesGuard]
      },
      {
        path: 'usage', data: {access: [5, 10]},
        loadChildren: () => import('./pages/statistics/statistics.module')
          .then(module => module.StatisticsModule),
        canActivate: [requiredRolesGuard]
      },
      {
        path: 'no-access', loadChildren: () => import('./pages/no-access/no-access.module')
          .then(module => module.NoAccessModule)
      }
    ]
  },
  {
    path: 'login', loadChildren: () => import('./pages/login/login.module')
      .then(module => module.LoginModule)
  },
  {
    path: 'forgot-password',
    loadChildren: () => import('./pages/forgot-password/forgot-password.module')
      .then(module => module.ForgotPasswordModule)
  },
  {
    path: 'reset-password/:token',
    loadChildren: () => import('./pages/reset-password/reset-password.module')
      .then(module => module.ResetPasswordModule)
  },
  {
    path: 'signup/:token', loadChildren: () => import('./pages/signup/signup.module')
      .then(module => module.SignupModule)
  },
  {
    path: 'multiview/:id', loadChildren: () => import('./pages/video-grid/video-grid.module')
      .then(module => module.VideoGridModule)
  },
  {
    path: 'multiview/:dns/:shortName', loadChildren: () => import('./pages/video-grid/video-grid.module')
      .then(module => module.VideoGridModule)
  },
  {
    path: 'grids/:id', loadChildren: () => import('./pages/video-grid/video-grid.module')
      .then(module => module.VideoGridModule)
  },
  {
    path: 'grids/:dns/:shortName', loadChildren: () => import('./pages/video-grid/video-grid.module')
      .then(module => module.VideoGridModule)
  },
  {
    path: 'choose-organization',
    loadChildren: () => import('./pages/choose-organization/choose-organization.module')
      .then(module => module.ChooseOrganizationModule)
  },
  {
    path: 'invite/:token',
    loadChildren: () => import('./pages/activate-invitation/activate-invitation.module')
      .then(module => module.ActivateInvitationModule)
  },
  {path: '**', redirectTo: 'clusters'}
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    useHash: true
  })],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
